import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { signInWithApple, signInWithGoogleAndroid, isNative, platform } from "@/lib/platform";
import { GoogleLogo, AppleLogo, Envelope, Lock, User, Gift, Eye, EyeSlash } from "@phosphor-icons/react";
import FounderBadge from "@/components/FounderBadge";

const REF_STORAGE_KEY = "hush_referral_code";

export default function AuthPage() {
  const [params] = useSearchParams();
  // Compute the initial mode synchronously so we don't have to setMode() from
  // inside a useEffect (which ESLint's react-hooks/set-state-in-effect rule
  // flags). If the user lands with ?ref=CODE we bias toward signup.
  const initialModeFromParams = (() => {
    if (params.get("mode") === "signup") return "signup";
    const fromUrl = (params.get("ref") || "").trim().toUpperCase();
    if (fromUrl) return "signup";
    return "login";
  })();
  const [mode, setMode] = useState(initialModeFromParams);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [referralCode, setReferralCode] = useState(() => {
    // Initial referral code: URL ?ref takes priority, then localStorage.
    try {
      const fromUrl = (new URLSearchParams(window.location.search).get("ref") || "").trim().toUpperCase();
      const stored = window.localStorage?.getItem(REF_STORAGE_KEY) || "";
      return fromUrl || stored;
    } catch (_) { return ""; }
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register, setUser } = useAuth();
  const navigate = useNavigate();

  // Persist any newly-seen referral code so it survives signup/login toggle
  // and Apple/Google redirects.
  useEffect(() => {
    if (referralCode) {
      try { localStorage.setItem(REF_STORAGE_KEY, referralCode); } catch (_) { /* noop */ }
    }
  }, [referralCode]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res =
      mode === "login"
        ? await login(email, password)
        : await register(email, password, name, referralCode || undefined);
    setLoading(false);
    if (res.ok) {
      try { localStorage.removeItem(REF_STORAGE_KEY); } catch (_) { /* noop */ }
      navigate("/app", { replace: true });
    } else {
      setErr(res.error);
    }
  };

  const handleGoogle = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (isNative() && platform() === "android") {
      setErr("");
      setLoading(true);
      try {
        const user = await signInWithGoogleAndroid(api);
        setUser(user);
        try { localStorage.removeItem(REF_STORAGE_KEY); } catch (_) { /* noop */ }
        navigate("/app", { replace: true });
      } catch (e) {
        const msg = String(e?.message || "");
        if (/CANCELLED|cancel/i.test(msg)) {
          // User dismissed the browser — no error toast.
        } else {
          setErr(formatApiError(e) || "Couldn't sign in with Google. Try email instead.");
        }
      } finally {
        setLoading(false);
      }
      return;
    }
    // Web: classic redirect flow handled by AuthCallback page.
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleApple = async () => {
    setErr("");
    setLoading(true);
    const telemetry = async (event, data) => {
      try {
        const payload = {
          message: `[apple-signin] ${event}: ${JSON.stringify(data || {}).slice(0, 500)}`,
          platform: "ios",
          app_version: "1.1.0",
          url: typeof window !== "undefined" ? window.location.href : "",
        };
        await Promise.allSettled([
          "https://momentum-app-64.preview.emergentagent.com/api/errors",
          "https://apphush.online/api/errors",
        ].map((u) =>
          fetch(u, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            credentials: "omit",
          }).catch(() => null)
        ));
      } catch (_) { /* noop */ }
    };
    try {
      await telemetry("native-start", {});
      const { identity_token, user: appleUser } = await signInWithApple();
      await telemetry("native-ok", { has_token: Boolean(identity_token), has_user: Boolean(appleUser) });
      // Extend timeout to 30s — /auth/apple has to fetch Apple's JWKS on the
      // backend which can take 5–10s on a cold start. Default 10s sometimes
      // throws axios "Network Error" before the backend even responds.
      const { data } = await api.post("/auth/apple", { identity_token, user: appleUser }, { timeout: 30000 });
      await telemetry("backend-ok", { user_id: data?.user_id });
      setUser(data);
      try { localStorage.removeItem(REF_STORAGE_KEY); } catch (_) { /* noop */ }
      navigate("/app", { replace: true });
    } catch (e) {
      const msg = String(e?.message || "");
      const status = e?.response?.status;
      const code = e?.code || "";
      await telemetry("fail", { msg: msg.slice(0, 200), status, code });
      // AuthorizationError 1000 = App ID is missing the "Sign in with Apple"
      // capability. AuthorizationError 1001 = user cancelled — silent.
      if (/1001/.test(msg) || /cancel/i.test(msg)) {
        // User cancelled — no error toast.
      } else if (/1000/.test(msg)) {
        setErr("Apple sign-in isn't set up yet. Use email + password for now.");
      } else {
        setErr(msg || formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  // Sign-in buttons configuration:
  // - WEB (apphush.online): "Continue with Google" via Emergent Auth redirect.
  //   Apple Sign-In on web requires Apple Services ID setup — skipped for v1.x.
  // - CAPACITOR iOS: "Continue with Apple" via @capacitor-community/apple-sign-in.
  //   Requires App ID has "Sign in with Apple" capability enabled in Apple
  //   Developer Portal AND the entitlements file lists it. If the capability
  //   isn't on, the button shows an inline error instead of failing silently.
  // - CAPACITOR Android: "Continue with Google" via Emergent Auth + Browser
  //   plugin + deep-link callback (app.hush.journal://auth-callback).
  const showGoogleButton = !isNative() || platform() === "android";
  const showAppleButton = isNative() && platform() === "ios";

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="auth-page">
      {/* Left: visual (web/tablet only) — pure CSS, no external dependencies */}
      <div
        className="hidden md:flex relative items-end p-12"
        style={{
          background:
            "linear-gradient(135deg, #f2ebe3 0%, #e6d8c8 35%, #c7a98b 70%, #b75d46 100%)",
        }}
      >
        <div className="absolute inset-0 bg-bone-100/30" aria-hidden="true" />
        <div className="relative z-10 max-w-md">
          <Link to="/" className="font-serif text-2xl text-ink-900">
            Hush
          </Link>
          <h2 className="font-serif text-4xl mt-10 leading-tight text-ink-900">
            Your thoughts deserve a <span className="italic text-brand">slower</span> place to land.
          </h2>
          <p className="mt-6 text-ink-700 leading-relaxed">
            A private voice journal that listens, transcribes, and reflects.
          </p>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center px-6 sm:px-12 py-16 bg-bone-100">
        <div className="w-full max-w-md">
          <Link to="/" className="font-serif text-xl text-ink-900 md:hidden block mb-10">
            Hush
          </Link>
          <p className="eyebrow mb-3">
            {mode === "login" ? "Welcome back" : "Begin your journal"}
          </p>
          <h1 className="font-serif text-4xl mb-8 leading-tight">
            {mode === "login" ? "Sign in to your journal" : "Create a quiet space"}
          </h1>

          {mode === "signup" && <FounderBadge variant="auth" />}

          {showGoogleButton && (
            <button
              type="button"
              onClick={handleGoogle}
              className="w-full btn-ghost flex items-center justify-center gap-3 mb-3"
              data-testid="google-auth-btn"
            >
              <GoogleLogo size={20} weight="bold" />
              Continue with Google
            </button>
          )}

          {showAppleButton && (
            <button
              type="button"
              onClick={handleApple}
              className="w-full bg-ink-900 text-bone-100 hover:bg-ink-700 transition-all rounded-full px-5 py-3 flex items-center justify-center gap-3 mb-3"
              data-testid="apple-auth-btn"
            >
              <AppleLogo size={20} weight="fill" />
              Continue with Apple
            </button>
          )}

          {(showGoogleButton || showAppleButton) && (
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-bone-300" />
              <span className="text-xs text-ink-500 uppercase tracking-widest">or</span>
              <div className="flex-1 h-px bg-bone-300" />
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && referralCode && (
              <div
                className="flex items-start gap-3 rounded-md border border-brand/30 bg-brand/[0.06] px-4 py-3"
                data-testid="referral-banner"
              >
                <Gift size={18} weight="fill" className="text-brand shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-ink-900">A friend invited you to Hush.</p>
                  <p className="text-ink-500 mt-0.5">
                    Sign up with code <code className="bg-bone-200 px-1 rounded text-ink-900">{referralCode}</code>{" "}
                    and you&apos;ll both get <b>30 days of Hush Plus, free</b>.
                  </p>
                </div>
              </div>
            )}
            {mode === "signup" && (
              <div>
                <label className="eyebrow block mb-2">Your name</label>
                <div className="relative">
                  <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-500" />
                  <input
                    data-testid="signup-name-input"
                    className="input pl-11"
                    placeholder="What should we call you?"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
              </div>
            )}
            <div>
              <label className="eyebrow block mb-2">Email</label>
              <div className="relative">
                <Envelope size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-500" />
                <input
                  data-testid="auth-email-input"
                  type="email"
                  className="input pl-11"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            <div>
              <label className="eyebrow block mb-2">Password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-500" />
                <input
                  data-testid="auth-password-input"
                  type={showPassword ? "text" : "password"}
                  className="input pl-11 pr-11"
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-ink-500 hover:text-ink-900 transition-colors"
                  data-testid="auth-password-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {err && (
              <div
                className="text-sm text-brand bg-brand/10 border border-brand/20 rounded-md px-3 py-2"
                data-testid="auth-error"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="auth-submit-btn"
            >
              {loading ? "Just a moment…" : mode === "login" ? "Sign in" : "Create my journal"}
            </button>
          </form>

          {mode === "login" && (
            <p className="mt-4 text-center">
              <Link to="/forgot-password" className="text-sm text-ink-500 hover:text-brand" data-testid="forgot-password-link">
                Forgot your password?
              </Link>
            </p>
          )}

          <p className="mt-8 text-sm text-ink-500 text-center">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button
              type="button"
              className="text-brand hover:underline font-medium"
              onClick={() => {
                setErr("");
                setMode(mode === "login" ? "signup" : "login");
              }}
              data-testid="auth-mode-toggle"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>

          <p className="mt-10 text-[11px] text-ink-500 text-center leading-relaxed">
            By continuing you agree to our{" "}
            <Link to="/terms" className="underline hover:text-ink-900">Terms</Link> and{" "}
            <Link to="/privacy" className="underline hover:text-ink-900">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
